import createImageUrlBuilder from '@sanity/image-url';
// import { Link } from '@/sanity.types';
// import { dataset, projectId, studioUrl } from '@/sanity/env';
import { dataset, projectId } from '../env';

// import { createDataAttribute, CreateDataAttributeProps } from 'next-sanity';

// ITSM-7871: deployed builds load Sanity assets from our own domain under
// /n_sanity/ so Cloudflare caches them (nginx proxies misses to cdn.sanity.io).
// Local dev (`next dev`) keeps loading straight from the Sanity CDN.
export const SANITY_ASSET_BASE_URL =
  process.env.NODE_ENV === 'production' && process.env.NEXT_PUBLIC_SITE_URL
    ? `${process.env.NEXT_PUBLIC_SITE_URL.replace(/\/+$/, '')}/n_sanity`
    : 'https://cdn.sanity.io';

// Only our own project/dataset is proxied; nginx 404s anything else.
const OWN_CDN_ASSET_PATTERN = `https://cdn\\.sanity\\.io/(?=(?:images|files)/${projectId}/${dataset}/)`;
const OWN_CDN_ASSET = new RegExp(`^${OWN_CDN_ASSET_PATTERN}`);
const OWN_CDN_ASSET_ANYWHERE = new RegExp(OWN_CDN_ASSET_PATTERN, 'g');

/**
 * Rewrites a Sanity CDN URL for our project/dataset (e.g. a GROQ `asset->url`)
 * to SANITY_ASSET_BASE_URL. Any other URL is returned unchanged.
 */
export const toEdgeAssetUrl = (url?: string | null) =>
  url ? url.replace(OWN_CDN_ASSET, `${SANITY_ASSET_BASE_URL}/`) : undefined;

const rewriteAssetUrlsDeep = (value: unknown): unknown => {
  if (typeof value === 'string') {
    return value.replace(OWN_CDN_ASSET_ANYWHERE, `${SANITY_ASSET_BASE_URL}/`);
  }
  if (Array.isArray(value)) return value.map(rewriteAssetUrlsDeep);
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([key, v]) => [key, rewriteAssetUrlsDeep(v)])
    );
  }
  return value;
};

/**
 * Rewrites every Sanity CDN URL for our project/dataset found anywhere in a
 * GROQ result (raw `asset->url` fields, links inside HTML strings) to
 * SANITY_ASSET_BASE_URL. Returns the data unchanged in local dev.
 */
export const rewriteSanityAssetUrls = <T>(data: T): T =>
  SANITY_ASSET_BASE_URL === 'https://cdn.sanity.io'
    ? data
    : (rewriteAssetUrlsDeep(data) as T);

/**
 * True for raster image formats that should be served as WebP. Accepts an
 * asset ref (`image-<hash>-<w>x<h>-png`) or a URL path (`.../<hash>-<w>x<h>.png`).
 * SVG and GIF are excluded: WebP would rasterize SVGs and drop GIF animation.
 */
export const isRasterImage = (refOrPath: string) =>
  /[.-](jpe?g|png|webp)$/i.test(refOrPath);

export const imageBuilder = createImageUrlBuilder({
  projectId: projectId || '',
  dataset: dataset || '',
  baseUrl: SANITY_ASSET_BASE_URL,
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const urlForImage = (source: any) => {
  // Ensure that source image contains a valid reference
  if (!source?.asset?._ref) {
    return undefined;
  }

  const image = imageBuilder?.image(source).fit('max').width(2000).quality(75);

  // An explicit format instead of auto('format'): Sanity varies auto=format
  // responses on the Accept header but Cloudflare's cache key doesn't, so a
  // cached AVIF could be served to a browser that can't decode it.
  return isRasterImage(source.asset._ref) ? image.format('webp') : image;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function resolveOpenGraphImage(image: any, width = 1200, height = 627) {
  if (!image?.asset?._ref) return;
  // Keep the original format: not every social crawler accepts WebP.
  const url = imageBuilder
    .image(image)
    .width(width)
    .height(height)
    .fit('crop')
    .quality(75)
    .url();
  if (!url) return;
  return { url, alt: image?.alt as string, width, height };
}

// Depending on the type of link, we need to fetch the corresponding page, post, or URL.  Otherwise return null.
// export function linkResolver(link: Link | undefined) {
//   if (!link) return null;

//   // If linkType is not set but href is, lets set linkType to "href".  This comes into play when pasting links into the portable text editor because a link type is not assumed.
//   if (!link.linkType && link.href) {
//     link.linkType = 'href';
//   }

//   switch (link.linkType) {
//     case 'href':
//       return link.href || null;
//     case 'page':
//       if (link?.page && typeof link.page === 'string') {
//         return `/${link.page}`;
//       }
//     case 'post':
//       if (link?.post && typeof link.post === 'string') {
//         return `/posts/${link.post}`;
//       }
//     default:
//       return null;
//   }
// }

// type DataAttributeConfig = CreateDataAttributeProps &
//   Required<Pick<CreateDataAttributeProps, 'id' | 'type' | 'path'>>;

// export function dataAttr(config: DataAttributeConfig) {
//   return createDataAttribute({
//     projectId,
//     dataset,
//     baseUrl: studioUrl,
//   }).combine(config);
// }

export const isVideoUrl = (url: string | undefined) => {
  if (!url) return false;
  // List of supported video platforms
  const videoPlatforms = [
    'youtube.com',
    'youtu.be',
    'vimeo.com',
    'dailymotion.com',
    'wistia.com',
  ];

  // List of supported video file extensions
  const videoExtensions = ['.mp4', '.mov', '.webm', '.ogg', '.avi'];

  try {
    const parsedUrl = new URL(url);

    // Check if the URL is from a supported video platform
    const isPlatformUrl = videoPlatforms.some((domain) =>
      parsedUrl.hostname.includes(domain)
    );

    // Check if the URL points to a video file
    const isVideoFile = videoExtensions.some((ext) =>
      parsedUrl.pathname.toLowerCase().endsWith(ext)
    );

    return isPlatformUrl || isVideoFile;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    return false; // Invalid URL
  }
};

export const isValidUrl = (string: string): boolean => {
  try {
    new URL(string);
    return true;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (err) {
    return false;
  }
};

// Sanity CDN base AI URL
const SANITY_CDN_BASE_URL = `${SANITY_ASSET_BASE_URL}/files/${projectId}/${dataset}`;

/**
 * Resolves the URL for .ai files based on the asset reference (_ref).
 * @param source - The asset object containing the _ref property.
 * @returns The URL for the .ai file or undefined if the reference is invalid.
 */
export const urlForAiFile = (source: { asset?: { _ref?: string } }) => {
  if (!source?.asset?._ref) {
    return undefined;
  }

  // Extract the file ID and extension from the _ref property
  const ref = source.asset._ref;
  const [fileId, extension] = ref.split('-').slice(1); // Example: "file-abc123-ai"

  if (!fileId || !extension) {
    return undefined; // Return undefined if the _ref format is invalid
  }

  // Construct the URL for the .ai file
  return `${SANITY_CDN_BASE_URL}/${fileId}.${extension}`;
};

/**
 * Resolves metadata for .ai files (URL, alt text, etc.).
 * @param source - The asset object containing the _ref property and optional alt text.
 * @param alt - Optional alt text for the file.
 * @returns An object containing the URL and alt text for the .ai file.
 */
export const resolveAiFileMetadata = (
  source: { asset?: { _ref?: string }; alt?: string },
  alt = 'Adobe Illustrator File'
) => {
  const url = urlForAiFile(source);
  if (!url) return undefined;

  return {
    url,
    alt: source?.alt || alt,
  };
};
